import React, { useState } from 'react';

import type { Leaves, Paths } from '@aline/common';
import { FormLabel } from '@mui/material';
import { FieldArray, Formik } from 'formik';
import type { CurrentUserType } from 'src/gql';
import * as yup from 'yup';

import { usePreOnboardingStepStyles } from './DemographicInfo';
import type { OnContinueFunction } from './OnboardingContainer';
import { FormikAlineInput } from '../FormikAlineInput';
import { AlineButton, Spacer } from '../generic';

interface TeamFormValues {
  existingOrgId: UUID | null;
  orgName: string;
  teamMembers: string[];
}

const validationSchema = yup.object().shape({
  orgName: yup.string().required('Organization name is required'),
  teamMembers: yup.array().of(yup.string().email('Invalid email address').nullable()),
});
const orgNameKey: Leaves<TeamFormValues> = 'orgName';
const teamMembersKey: Paths<TeamFormValues> = 'teamMembers';

export function TeamSetup({
  currentUser,
  onBack,
  onContinue,
}: {
  currentUser: CurrentUserType;
  onBack: () => void;
  onContinue: OnContinueFunction;
}) {
  const { classes } = usePreOnboardingStepStyles();
  const [submitting, setSubmitting] = useState<boolean>(false);

  return (
    <div className={classes.container}>
      <div className={classes.formWrapper}>
        <Formik<TeamFormValues>
          enableReinitialize
          initialValues={{
            existingOrgId: currentUser?.org?.id ?? null,
            orgName: currentUser?.org?.name ?? '',
            teamMembers: ['', '', '', '', ''],
          }}
          validationSchema={validationSchema}
          onSubmit={async values => {
            setSubmitting(true);
            await onContinue({
              existingOrgId: values.existingOrgId,
              orgName: values.orgName,
              stepIndex: 1,
              teamMembers: values.teamMembers.filter(email => email !== ''),
            });
            setSubmitting(false);
          }}
        >
          {({ handleReset, handleSubmit, isValid, values }) => {
            return (
              <form className={classes.form} onReset={handleReset} onSubmit={handleSubmit}>
                <FormLabel className={classes.formLabel}>Org name *</FormLabel>
                <FormikAlineInput
                  fullWidth
                  required
                  fieldName={orgNameKey}
                  inputClassName={classes.input}
                  placeholder="Type your organization name..."
                />
                <FormLabel className={classes.formLabel}>Add team members</FormLabel>
                <FieldArray name="teamMembers">
                  {({ push }) => (
                    <>
                      {values.teamMembers.map((_, index) => (
                        <FormikAlineInput
                          key={index}
                          fullWidth
                          fieldName={`${teamMembersKey}.${index}`}
                          inputClassName={classes.input}
                          placeholder="Invite by email address (e.g., johndoe@email.com)..."
                        />
                      ))}
                      <AlineButton
                        style={{ marginBottom: 16 }}
                        variant="outlined"
                        onClick={() => push('')}
                      >
                        + Add another member
                      </AlineButton>
                    </>
                  )}
                </FieldArray>
                <Spacer />
                <div className={classes.buttons}>
                  <AlineButton variant="outlined" onClick={onBack}>
                    Go Back
                  </AlineButton>
                  <AlineButton
                    color="primary"
                    disabled={!isValid}
                    loading={submitting}
                    type="submit"
                    variant="contained"
                  >
                    Continue
                  </AlineButton>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
}
